import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DossierService, AgentService } from '../../services/dossier.service';
import { AuthService } from '../../services/auth.service';
import { Dossier, Document, DossierCheck, CheckStatus, DossierAlert, DiscrepancyKpi, DiscrepancyCatalogEntry } from '../../models/dossier.model';

interface ChecksByCategory {
  category: string;
  checks: DossierCheck[];
}

@Component({
  selector: 'app-dossier-verification',
  templateUrl: './dossier-verification.component.html',
  styleUrls: ['./dossier-verification.component.css'],
})
export class DossierVerificationComponent implements OnInit, OnDestroy {

  dossierId = '';
  dossier: Dossier | null = null;
  documents: Document[] = [];
  checks: DossierCheck[] = [];
  checksByCategory: ChecksByCategory[] = [];

  loading = false;
  error = '';
  savingCheck: string | null = null;
  savingOriginal: string | null = null;

  /** Track which check's notes are being edited */
  editingNotes: string | null = null;
  editNotesValue = '';

  /** Active tab: 'originals', 'checks' (Due Diligence Checks) or 'discrepancies' */
  activeTab: 'originals' | 'checks' | 'discrepancies' = 'originals';

  /** Discrepancies (UCP-ISBP): list, KPI, catalog and manual-add form state. */
  alerts: DossierAlert[] = [];
  discrepancyKpi: DiscrepancyKpi = { auto: 0, manual: 0, total: 0 };
  discrepancyCatalog: DiscrepancyCatalogEntry[] = [];
  savingAlert: string | null = null;
  detectingDiscrepancies = false;
  discrepancyError = '';

  /** Manual-add form. */
  showAddDiscrepancy = false;
  addingDiscrepancy = false;
  newDiscrepancyCatalogId = '';
  newDiscrepancyDescription = '';

  /** First-page thumbnail blob URLs keyed by document OID */
  pageImageUrls: Record<string, string> = {};

  /** Viewer panel state */
  viewerDoc: Document | null = null;
  viewerPageIndex = 0;
  viewerCurrentUrl: string | null = null;
  viewerPanelWidth = 420;
  viewerOpen = true;
  private viewerCache: Record<string, string> = {};
  private resizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = 0;

  /** Auto-verify state */
  autoVerifying = false;
  autoVerifyError = '';
  autoVerifyModel = '';
  autoVerifyResult: { model: string; auto_verified: number; usage: { input: number; output: number } } | null = null;

  /** Available compliance models — loaded from server */
  complianceModels: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dossierService: DossierService,
    private agentService: AgentService,
    public authService: AuthService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.dossierId = this.route.snapshot.paramMap.get('id') || '';
    if (this.dossierId) {
      this.loadData();
      this.loadModels();
      this.checkTfAgentAvailability();
    }
  }

  private loadModels(): void {
    this.dossierService.getModels().subscribe({
      next: (res) => { this.complianceModels = res.models || []; },
      error: () => {},
    });
  }

  loadData(): void {
    this.loading = true;
    this.error = '';

    // Load dossier + all documents across uploads
    this.dossierService.getDossier(this.dossierId).subscribe({
      next: (dossier) => {
        this.dossier = dossier;
        this.loadAllDocuments(dossier);
        this.loadChecks();
        this.loadDiscrepancies();
        this.loadDiscrepancyCatalog();
      },
      error: (err) => {
        this.error = err?.error?.detail || 'Failed to load dossier';
        this.loading = false;
      },
    });
  }

  private loadAllDocuments(dossier: Dossier): void {
    if (!dossier.uploads || dossier.uploads.length === 0) {
      this.documents = [];
      this.loading = false;
      return;
    }

    let pending = dossier.uploads.length;
    const allDocs: Document[] = [];

    for (const upload of dossier.uploads) {
      this.dossierService.getDocuments(this.dossierId, upload.oid).subscribe({
        next: (docs) => {
          // Tag each doc with its upload info for display
          for (const d of docs) {
            (d as any)._uploadFilename = upload.original_filename;
            (d as any)._uploadOid = upload.oid;
          }
          allDocs.push(...docs);
          pending--;
          if (pending === 0) {
            this.documents = allDocs;
            this.loading = false;
            this.loadThumbnails();
          }
        },
        error: () => {
          pending--;
          if (pending === 0) {
            this.documents = allDocs;
            this.loading = false;
            this.loadThumbnails();
          }
        },
      });
    }
  }

  private loadThumbnails(): void {
    for (const doc of this.documents) {
      const firstPage = doc.pages?.[0];
      if (!firstPage || this.pageImageUrls[doc.oid]) { continue; }
      this.dossierService.getPageImage(this.dossierId, (doc as any)._uploadOid, firstPage.oid)
        .subscribe({
          next: (blob) => { this.pageImageUrls[doc.oid] = URL.createObjectURL(blob); },
          error: () => {},
        });
    }
    if (this.documents.length > 0 && !this.viewerDoc) {
      this.openViewer(this.documents[0]);
    }
  }

  ngOnDestroy(): void {
    Object.values(this.pageImageUrls).forEach(url => URL.revokeObjectURL(url));
    Object.values(this.viewerCache).forEach(url => URL.revokeObjectURL(url));
  }

  private loadChecks(): void {
    this.dossierService.getDossierChecks(this.dossierId).subscribe({
      next: (checks) => {
        this.checks = checks;
        this.groupChecksByCategory();
      },
      error: (err) => {
        console.error('Failed to load checks', err);
      },
    });
  }

  private groupChecksByCategory(): void {
    const map = new Map<string, DossierCheck[]>();
    for (const c of this.checks) {
      const list = map.get(c.check_category) || [];
      list.push(c);
      map.set(c.check_category, list);
    }
    this.checksByCategory = Array.from(map.entries()).map(([category, checks]) => ({
      category,
      checks,
    }));
  }

  // --- Discrepancies (UCP-ISBP) ---

  private loadDiscrepancies(): void {
    this.dossierService.getDossierAlerts(this.dossierId).subscribe({
      next: (alerts) => { this.alerts = alerts; },
      error: (err) => { console.error('Failed to load discrepancies', err); },
    });
    this.dossierService.getDiscrepancyKpi(this.dossierId).subscribe({
      next: (kpi) => { this.discrepancyKpi = kpi; },
      error: () => {},
    });
  }

  private loadDiscrepancyCatalog(): void {
    this.dossierService.getDiscrepancyCatalog().subscribe({
      next: (entries) => { this.discrepancyCatalog = entries; },
      error: () => {},
    });
  }

  // --- Explainability: resolve a UCP-ISBP catalog id (ucpisbp_NNN) to its
  //     UCP article ref / description, used in both the Examination checklist
  //     and the Discrepancies table so every implemented check is verifiable.
  private catalogEntry(id: string | null | undefined): DiscrepancyCatalogEntry | undefined {
    if (!id) { return undefined; }
    return this.discrepancyCatalog.find(e => e.id === id);
  }

  /** UCP article reference for a catalog id (e.g. "Article 6"), or ''. */
  catalogUcp(id: string): string {
    const e = this.catalogEntry(id);
    return e && e.ucp_ref && e.ucp_ref !== '(none)' ? e.ucp_ref : '';
  }

  /** Catalog description for a catalog id (used as tooltip). */
  catalogDesc(id: string): string {
    const e = this.catalogEntry(id);
    return e ? e.description : id;
  }

  /** True when a discrepancy's rule_name references a UCP-ISBP catalog entry. */
  isCatalogRule(ruleName: string | null): boolean {
    return !!ruleName && /^ucpisbp_\d+$/.test(ruleName);
  }

  /** Percentage of discrepancies that were auto-detected (0 when none). */
  get autoDetectedPercent(): number {
    if (!this.discrepancyKpi.total) { return 0; }
    return Math.round((this.discrepancyKpi.auto / this.discrepancyKpi.total) * 100);
  }

  get manualDetectedPercent(): number {
    if (!this.discrepancyKpi.total) { return 0; }
    return 100 - this.autoDetectedPercent;
  }

  /** Run the cross-document discrepancy detection engine. */
  runDetectDiscrepancies(): void {
    this.detectingDiscrepancies = true;
    this.discrepancyError = '';
    this.dossierService.detectDiscrepancies(this.dossierId).subscribe({
      next: () => {
        this.detectingDiscrepancies = false;
        this.loadDiscrepancies();
      },
      error: (err) => {
        this.detectingDiscrepancies = false;
        this.discrepancyError = err?.error?.detail || 'Failed to detect discrepancies';
      },
    });
  }

  /** When a catalog entry is picked, prefill the description with its text. */
  onCatalogSelect(): void {
    const entry = this.discrepancyCatalog.find(e => e.id === this.newDiscrepancyCatalogId);
    if (entry && !this.newDiscrepancyDescription.trim()) {
      this.newDiscrepancyDescription = entry.description;
    }
  }

  /** Add a manual discrepancy (works even when all checks pass). */
  addManualDiscrepancy(): void {
    const description = this.newDiscrepancyDescription.trim();
    if (!description) {
      this.discrepancyError = 'A description is required.';
      return;
    }
    this.addingDiscrepancy = true;
    this.discrepancyError = '';
    this.dossierService.createDossierAlert(this.dossierId, {
      description,
      rule_name: this.newDiscrepancyCatalogId || null,
    }).subscribe({
      next: () => {
        this.addingDiscrepancy = false;
        this.showAddDiscrepancy = false;
        this.newDiscrepancyCatalogId = '';
        this.newDiscrepancyDescription = '';
        this.loadDiscrepancies();
      },
      error: (err) => {
        this.addingDiscrepancy = false;
        this.discrepancyError = err?.error?.detail || 'Failed to add discrepancy';
      },
    });
  }

  /** Update a discrepancy's status. */
  setAlertStatus(alert: DossierAlert, status: string): void {
    this.savingAlert = alert.oid;
    this.dossierService.updateDossierAlert(this.dossierId, alert.oid, status, alert.resolved_notes).subscribe({
      next: (updated) => {
        const idx = this.alerts.findIndex(a => a.oid === updated.oid);
        if (idx >= 0) { this.alerts[idx] = updated; }
        this.savingAlert = null;
        this.loadDiscrepancies();
      },
      error: (err) => {
        this.savingAlert = null;
        this.discrepancyError = err?.error?.detail || 'Failed to update discrepancy';
      },
    });
  }

  // --- Originality ---

  getUploadFilename(doc: Document): string {
    return (doc as any)._uploadFilename || '';
  }

  getUploadOid(doc: Document): string {
    return (doc as any)._uploadOid || '';
  }

  setOriginal(doc: Document, value: boolean | null): void {
    if (this.savingOriginal === doc.oid) return;
    const previous = doc.is_original;
    doc.is_original = value;
    this.savingOriginal = doc.oid;

    this.dossierService.updateDocumentOriginal(
      this.dossierId,
      this.getUploadOid(doc),
      doc.oid,
      value,
    ).subscribe({
      next: () => { this.savingOriginal = null; },
      error: () => {
        doc.is_original = previous;
        this.savingOriginal = null;
      },
    });
  }

  /**
   * True when the document set changed after the last examination run (D4).
   * The examination is never re-run automatically — it is a paid LLM call — so
   * the UI just says the results are out of date and offers the re-run.
   */
  get examinationStale(): boolean {
    return !!this.dossier?.examination_stale_at;
  }

  /** Document types gated out of extraction/examination (see classification_gate.py). */
  private static readonly GATED_TYPES = ['IGNORE', 'UNCLASSIFIED'];

  /** Bulk-flag every document (except gated pages) as Original or Copy. */
  flaggingAll = false;
  flagAll(value: boolean): void {
    if (this.flaggingAll) return;
    this.flaggingAll = true;
    this.dossierService.flagAllOriginal(this.dossierId, value).subscribe({
      next: () => {
        for (const doc of this.documents) {
          if (!DossierVerificationComponent.GATED_TYPES.includes((doc.document_type || '').toUpperCase())) {
            doc.is_original = value;
          }
        }
        this.flaggingAll = false;
      },
      error: () => { this.flaggingAll = false; },
    });
  }

  originalLabel(doc: Document): string {
    if (doc.is_original === true) return 'Original';
    if (doc.is_original === false) return 'Copy';
    return 'Not assessed';
  }

  originalClass(doc: Document): string {
    if (doc.is_original === true) return 'original';
    if (doc.is_original === false) return 'copy';
    return 'unassessed';
  }

  // --- Checks ---

  setCheckStatus(check: DossierCheck, status: CheckStatus): void {
    if (this.savingCheck === check.check_id) return;
    this.savingCheck = check.check_id;

    this.dossierService.updateDossierCheck(
      this.dossierId,
      check.check_id,
      status,
      check.notes,
    ).subscribe({
      next: (updated) => {
        check.status = updated.status;
        check.notes = updated.notes;
        check.updated_at = updated.updated_at;
        this.savingCheck = null;
      },
      error: () => {
        this.savingCheck = null;
      },
    });
  }

  startEditNotes(check: DossierCheck): void {
    this.editingNotes = check.check_id;
    this.editNotesValue = check.notes || '';
  }

  saveNotes(check: DossierCheck): void {
    this.editingNotes = null;
    const notes = this.editNotesValue.trim() || null;
    if (notes === check.notes) return;

    this.savingCheck = check.check_id;
    this.dossierService.updateDossierCheck(
      this.dossierId,
      check.check_id,
      check.status,
      notes,
    ).subscribe({
      next: (updated) => {
        check.notes = updated.notes;
        this.savingCheck = null;
      },
      error: () => {
        this.savingCheck = null;
      },
    });
  }

  cancelEditNotes(): void {
    this.editingNotes = null;
  }

  checkStatusIcon(status: CheckStatus): string {
    switch (status) {
      case 'ok': return '✓';
      case 'fail': return '✗';
      case 'na': return '—';
      default: return '○';
    }
  }

  checkStatusClass(status: CheckStatus): string {
    return `check-${status}`;
  }

  // --- Summary ---

  get totalChecks(): number { return this.checks.length; }
  get okChecks(): number { return this.checks.filter(c => c.status === 'ok').length; }
  get failChecks(): number { return this.checks.filter(c => c.status === 'fail').length; }
  get pendingChecks(): number { return this.checks.filter(c => c.status === 'pending').length; }
  get naChecks(): number { return this.checks.filter(c => c.status === 'na').length; }

  get totalDocs(): number { return this.documents.length; }
  get originalDocs(): number { return this.documents.filter(d => d.is_original === true).length; }
  get copyDocs(): number { return this.documents.filter(d => d.is_original === false).length; }
  get unassessedDocs(): number { return this.documents.filter(d => d.is_original == null).length; }

  // --- Auto-verify ---

  runAutoVerify(): void {
    if (this.autoVerifying) return;
    this.autoVerifying = true;
    this.autoVerifyError = '';
    this.autoVerifyResult = null;

    const model = this.autoVerifyModel || null;

    this.dossierService.autoVerifyChecks(this.dossierId, model).subscribe({
      next: (result) => {
        this.checks = result.checks;
        this.groupChecksByCategory();
        this.autoVerifyResult = {
          model: result.model,
          auto_verified: result.auto_verified,
          usage: result.usage,
        };
        // The examination now matches the current document set again (D4).
        if (this.dossier) {
          this.dossier.examination_stale_at = null;
        }
        this.autoVerifying = false;
      },
      error: (err) => {
        this.autoVerifyError = err?.error?.detail || 'Auto-verification failed';
        this.autoVerifying = false;
      },
    });
  }

  displayModel(m: string): string {
    // Strip bedrock/ prefix and shorten for display
    if (m.startsWith('bedrock/')) {
      const bare = m.substring(8);
      // Strip eu./us. prefix
      const stripped = bare.replace(/^(eu|us|ap)\./, '');
      return stripped;
    }
    return m;
  }

  // --- TF Compliance Agent Chat ---

  @ViewChild('tfChatMessages') tfChatMessages!: ElementRef;
  tfAgentAvailable = false;
  tfChatOpen = false;
  tfMessages: { role: 'user' | 'assistant'; content: string }[] = [];
  tfSessionId: string | null = null;
  tfInput = '';
  tfSending = false;

  private checkTfAgentAvailability(): void {
    this.agentService.tfComplianceStatus().subscribe({
      next: (res) => { this.tfAgentAvailable = res.available; },
      error: () => { this.tfAgentAvailable = false; },
    });
  }

  toggleTfChat(): void {
    this.tfChatOpen = !this.tfChatOpen;
  }

  sendTfMessage(): void {
    const msg = this.tfInput.trim();
    if (!msg || this.tfSending) return;

    this.tfInput = '';
    this.tfSending = true;
    this.tfMessages.push({ role: 'user', content: msg });

    this.agentService.chatTfCompliance(msg, this.dossierId, this.tfSessionId || undefined).subscribe({
      next: (res) => {
        this.tfSessionId = res.session_id;
        this.tfMessages.push({ role: 'assistant', content: res.response });
        this.tfSending = false;
        this.scrollTfChat();
        // Reload checks in case the agent updated any
        this.loadChecks();
      },
      error: (err) => {
        this.tfMessages.push({ role: 'assistant', content: err?.error?.detail || 'Agent error.' });
        this.tfSending = false;
        this.scrollTfChat();
      },
    });
    this.scrollTfChat();
  }

  onTfKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendTfMessage();
    }
  }

  private scrollTfChat(): void {
    setTimeout(() => {
      if (this.tfChatMessages) {
        const el = this.tfChatMessages.nativeElement;
        el.scrollTop = el.scrollHeight;
      }
    }, 50);
  }

  // --- Document Viewer Panel ---

  openViewer(doc: Document): void {
    if (this.viewerDoc?.oid === doc.oid) return;
    this.viewerDoc = doc;
    this.viewerPageIndex = 0;
    this.viewerOpen = true;
    this.loadViewerPage();
  }

  closeViewer(): void {
    this.viewerOpen = false;
  }

  toggleViewer(): void {
    this.viewerOpen = !this.viewerOpen;
    if (this.viewerOpen && this.viewerDoc && !this.viewerCurrentUrl) {
      this.loadViewerPage();
    }
  }

  selectViewerDoc(docOid: string): void {
    const doc = this.documents.find(d => d.oid === docOid);
    if (doc) {
      this.viewerDoc = doc;
      this.viewerPageIndex = 0;
      this.loadViewerPage();
    }
  }

  get viewerPageCount(): number {
    return this.viewerDoc?.pages?.length || 0;
  }

  viewerPrevPage(): void {
    if (this.viewerPageIndex > 0) {
      this.viewerPageIndex--;
      this.loadViewerPage();
    }
  }

  viewerNextPage(): void {
    if (this.viewerPageIndex < this.viewerPageCount - 1) {
      this.viewerPageIndex++;
      this.loadViewerPage();
    }
  }

  private loadViewerPage(): void {
    const pages = this.viewerDoc?.pages || [];
    const page = pages[this.viewerPageIndex];
    if (!page) { this.viewerCurrentUrl = null; return; }

    const cacheKey = page.oid;
    if (this.viewerCache[cacheKey]) {
      this.viewerCurrentUrl = this.viewerCache[cacheKey];
      return;
    }

    this.viewerCurrentUrl = null;
    const uploadOid = this.getUploadOid(this.viewerDoc!);
    this.dossierService.getPageImage(this.dossierId, uploadOid, page.oid).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        this.viewerCache[cacheKey] = url;
        if (this.viewerDoc?.pages?.[this.viewerPageIndex]?.oid === cacheKey) {
          this.viewerCurrentUrl = url;
        }
      },
      error: () => {},
    });
  }

  onResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.resizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.viewerPanelWidth;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.resizing) return;
      const delta = this.resizeStartX - e.clientX;
      this.viewerPanelWidth = Math.max(250, Math.min(800, this.resizeStartWidth + delta));
    };

    const onMouseUp = () => {
      this.resizing = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }

  goBack(): void {
    this.router.navigate(['/dossiers', this.dossierId]);
  }
}
